import React, { useMemo, useState } from 'react';
import { DeleteOutline, EditOutlined } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useDebounce } from 'usehooks-ts';
import {
  getGetAllTagTypesQueryKey,
  getGetAllTagsQueryKey,
  useDeleteTag,
  useDeleteTagType,
  useGetAllTagTypes,
  useGetAllTags,
} from '@juno/client-api';
import { Site, Tag, TagType } from '@juno/client-api/model';
import { ConfirmDeleteDialog, Container, DefaultSearchSortFilter, JunoDialog } from '@juno/ui';
import {
  FEATURE_FLAG,
  MutationAction,
  onMutation,
  useFeatureFlagWithUser,
  useSettings,
} from '@juno/utils';
import CreateTagOrTagTypeForm from './CreateTagOrTagTypeForm';

const dummyArr = [0, 1, 2, 3, 4, 5, 6, 7];
interface TagsPanelProps {
  site: Site;
}

const TagsPanel: React.FC<TagsPanelProps> = ({ site }) => {
  const { id: siteId } = site;
  const [isSaving, setIsSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<Tag | undefined>(undefined);
  const [editingType, setEditingType] = useState<TagType | undefined>(undefined);
  const [deleteTypeOpen, setDeleteTypeOpen] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const queryClient = useQueryClient();
  const { value: directoryTagFiltersValue } = useFeatureFlagWithUser(
    FEATURE_FLAG.DIRECTORY_TAG_FILTERS,
  );
  const { data: tags, isLoading: isLoadingTags } = useGetAllTags(siteId, {
    order: 'type__value,value',
  });
  const { data: tagTypes, isLoading: isLoadingTagTypes } = useGetAllTagTypes(siteId, {
    order: 'value',
  });
  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: getGetAllTagsQueryKey(siteId) });
    queryClient.invalidateQueries({ queryKey: getGetAllTagTypesQueryKey(siteId) });
  };

  const deleteTagType = useDeleteTagType(onMutation(MutationAction.DELETE, 'TagType', refetch));

  const isLoading = isLoadingTags || isLoadingTagTypes;

  const handleCancelEdit = async () => {
    await setCreateOpen(false);

    // Delay clearing the selected tag to prevent text flicker between create/update
    setTimeout(() => {
      setSelectedTag(undefined);
      setEditingType(undefined);
    }, 300);
  };

  const handleDeleteTagType = async () => {
    await deleteTagType.mutateAsync({
      siteId,
      tagTypeId: deleteTypeOpen || '',
    });

    setDeleteTypeOpen('');
  };

  const filteredTags = useMemo(
    () => tags?.filter((t) => t.value.toLowerCase().includes(debouncedSearch.toLowerCase())) || [],
    [tags, debouncedSearch],
  );

  return (
    <Container>
      <Card sx={{ p: 2, mt: 2, mb: 4 }}>
        <Typography variant='h5'>Tags</Typography>
        <Divider sx={{ mt: 2, mb: 4 }} />
        <Typography variant='body2' sx={{ mt: 2, mb: 4 }}>
          Tags organize the content on your platform. They are essential for filtering and
          searching.
        </Typography>
        <DefaultSearchSortFilter
          buttonDisabled={false}
          buttonText={'New'}
          onClickButton={() => setCreateOpen(true)}
          setSearch={(value: string) => setSearch(value)}
          setSort={() => {}}
          showFilter={false}
          showSort={false}
        />

        {!isLoading &&
          tagTypes?.map((tt) => (
            <Accordion
              defaultExpanded
              elevation={0}
              sx={{
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='panel1-content'
                id='panel1-header'
                sx={{
                  p: 0,
                  m: 0,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  minHeight: 0,
                  '.MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded, .MuiAccordionSummary-root':
                    {
                      margin: 0,
                      minHeight: 0,
                    },
                  '&.Mui-expanded': {
                    minHeight: 0,
                  },
                }}
              >
                <Stack
                  direction='row'
                  justifyContent='space-between'
                  alignItems={'center'}
                  p={1}
                  height={54}
                  width={'100%'}
                >
                  <>
                    <Typography variant='h6'>
                      {tt.value} ({filteredTags?.filter((t) => t.type?.id === tt?.id).length})
                    </Typography>
                    {directoryTagFiltersValue && (
                      <Box>
                        <IconButton
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setEditingType(tt);
                            setCreateOpen(true);
                          }}
                          aria-label={`Edit ${tt.value} Tag Type button`}
                          size='small'
                        >
                          <EditOutlined />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setDeleteTypeOpen(tt.id);
                          }}
                          size='small'
                          aria-label={`Delete ${tt.value} Tag Type button`}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </Box>
                    )}
                  </>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {filteredTags?.map((tag, i) => {
                  if (tag.type.id === tt.id) {
                    return (
                      <TagRow
                        tag={tag}
                        key={tag.id}
                        i={i}
                        handleEdit={async () => {
                          await setSelectedTag(tag);
                          setCreateOpen(true);
                        }}
                        refetchTags={() => refetch()}
                      />
                    );
                  }
                })}
              </AccordionDetails>
            </Accordion>
          ))}

        {isLoading && dummyArr.map((item, i) => <SkeletonTile i={i} key={i} />)}

        <CreateTagOrTagTypeForm
          site={site}
          tags={tags}
          tagTypes={tagTypes}
          handleCancelEdit={handleCancelEdit}
          refetch={refetch}
          selectedTag={selectedTag}
          selectedTagType={editingType}
          open={createOpen}
        />
      </Card>

      <JunoDialog
        id='delete-tag-type'
        open={!!deleteTypeOpen}
        title='Delete Tag Type?'
        subtitle='Are you sure you want to delete this tag type?'
        message='This will permanently delete all tags of this type as well.'
        primaryBtnProps={{
          onClick: () => {
            handleDeleteTagType();
          },
          title: 'Delete',
          loading: deleteTagType.isPending,
        }}
        secondaryBtnProps={{
          onClick: () => {
            setDeleteTypeOpen('');
          },
          title: 'Cancel',
        }}
        mode='delete'
      />
    </Container>
  );
};

export default TagsPanel;
interface TagRowProps {
  tag: Tag;
  i: number;
  handleEdit: (tag: Tag) => void;
  refetchTags: () => void;
}
export const TagRow: React.FC<TagRowProps> = ({ tag, i, handleEdit, refetchTags }) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteTag = useDeleteTag(onMutation(MutationAction.DELETE, 'Tag', () => {}));
  const { site } = useSettings();

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTag.mutateAsync({ siteId: site?.id || '', tagId: tag.id });
      refetchTags();
    } catch (e) {
      setIsDeleting(false);
      console.error(e);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Stack
        direction='row'
        p={1}
        sx={{
          ml: 3,
          borderRadius: 1,
          alignItems: 'center',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography sx={{ width: 450 }}>{tag.value}</Typography>
        {/* <Typography>{tag.type.value}</Typography> */}
        <Box ml={'auto'}>
          <IconButton
            onClick={() => handleEdit(tag)}
            size='small'
            aria-label={`Edit ${tag.value} Tag button`}
          >
            <EditOutlined />
          </IconButton>
          <IconButton
            size='small'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDeleteOpen(true);
            }}
            aria-label={`Delete ${tag.value} Tag button`}
          >
            <DeleteOutline />
          </IconButton>
        </Box>
      </Stack>
      <JunoDialog
        id='delete-tag'
        open={deleteOpen}
        title='Delete Tag?'
        subtitle='Are you sure you want to delete this tag?'
        message='This action cannot be undone.'
        primaryBtnProps={{
          onClick: () => {
            setDeleteOpen(false);
            handleDelete();
          },
          loading: isDeleting,
          title: 'Delete',
        }}
        secondaryBtnProps={{
          onClick: () => {
            setDeleteOpen(false);
          },
          title: 'Cancel',
        }}
        mode='delete'
      />
    </>
  );
};

const SkeletonTile = ({ i }: { i: number }) => {
  return (
    <Stack
      direction='row'
      height={36}
      sx={{ background: i % 2 !== 0 ? 'rgba(0,0,0,.05)' : 'transparent' }}
      alignItems='center'
    >
      <Box width={300}>
        <Skeleton variant='text' width={190} />
      </Box>
      <Box>
        <Skeleton variant='text' width={100} />
      </Box>
    </Stack>
  );
};
